/**
 * S3 -> CloudFront URL rewriting.
 *
 * Uploads are persisted with whatever absolute URL the backend returns, which
 * is still the raw S3 virtual-hosted host (`<bucket>.s3.amazonaws.com`). These
 * helpers map any known bucket host onto its CDN origin so that both newly
 * saved content and pre-existing rows are served from the edge.
 *
 * Call sites do not need to know which bucket a URL belongs to - the mapping is
 * resolved from the host.
 */

type CdnMapping = {
  /** `<bucket>.<endpoint>`, e.g. `iot-gse-preprod-useast1-gse.s3.amazonaws.com` */
  bucketHost: string;
  /** CDN origin including scheme, without a trailing slash */
  cdnBase: string;
};

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '');

const buildMapping = (
  bucket?: string,
  endpoint?: string,
  cdn?: string,
): CdnMapping | null => {
  if (!bucket || !endpoint || !cdn) return null;
  return {
    bucketHost: `${bucket}.${endpoint}`,
    cdnBase: trimTrailingSlash(cdn),
  };
};

/**
 * Built once at module load. Entries whose env vars are missing are dropped, so
 * an unconfigured bucket degrades to "leave the URL alone" rather than
 * producing a broken `undefined/...` URL.
 */
export const CDN_MAPPINGS: CdnMapping[] = [
  buildMapping(
    process.env.REACT_APP_GSE_S3_BUCKET,
    process.env.REACT_APP_GSE_S3_ENDPOINT,
    process.env.REACT_APP_GSE_CLOUDFRONT,
  ),
  buildMapping(
    process.env.REACT_APP_ASSETS_S3_BUCKET,
    process.env.REACT_APP_ASSETS_S3_ENDPOINT,
    process.env.REACT_APP_ASSETS_CLOUDFRONT,
  ),
  buildMapping(
    process.env.REACT_APP_MALL_S3_BUCKET,
    process.env.REACT_APP_MALL_S3_ENDPOINT,
    process.env.REACT_APP_MALL_CLOUDFRONT,
  ),
  buildMapping(
    process.env.REACT_APP_STCMS_S3_BUCKET,
    // st-cms has no dedicated endpoint var; it shares the standard S3 endpoint.
    process.env.REACT_APP_STCMS_S3_ENDPOINT || 's3.amazonaws.com',
    process.env.REACT_APP_STCMS_CLOUDFRONT,
  ),
].filter((mapping): mapping is CdnMapping => mapping !== null);

/**
 * Matches `https://host`, `http://host` and protocol-relative `//host`, so all
 * three legacy forms normalise onto the CDN's own scheme.
 */
const hostPattern = (bucketHost: string) =>
  new RegExp(`(?:https?:)?//${bucketHost.replace(/\./g, '\\.')}`, 'gi');

/**
 * Rewrites a single URL from its S3 bucket host to the matching CDN origin.
 *
 * Returns the input untouched when it is empty or when no bucket host matches,
 * and is idempotent - applying it to an already-rewritten URL is a no-op. That
 * matters because the same URL may pass through both the write path and the
 * read path.
 */
export const toCdnUrl = (url?: string | null): string => {
  if (!url || typeof url !== 'string') return url ?? '';

  for (const { bucketHost, cdnBase } of CDN_MAPPINGS) {
    if (url.indexOf(bucketHost) === -1) continue;
    return url.replace(hostPattern(bucketHost), cdnBase);
  }

  return url;
};

/**
 * Only `{}`-style objects are safe to rebuild key by key. Blobs, Files, Dates,
 * FormData and similar carry state that is not reachable via `Object.keys`, so
 * rebuilding them would silently produce an empty object.
 */
const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/**
 * Recursively rewrites every string in a parsed response body.
 *
 * Used by the st-cms response interceptor, where image URLs are not confined to
 * known fields - they also appear inside nested page sections and rich-text
 * content. Anything that is not a string, array or plain object is returned
 * untouched.
 */
export const toCdnDeep = <T>(value: T): T => {
  if (typeof value === 'string') {
    return toCdnUrl(value) as unknown as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => toCdnDeep(item)) as unknown as T;
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      result[key] = toCdnDeep(value[key]);
    }
    return result as unknown as T;
  }

  return value;
};
